import { useState, useEffect } from 'react'
import { api } from '../../lib/api'
import { CheckCircle, XCircle, Clock, User } from 'lucide-react'
import toast from 'react-hot-toast'
import AdminNavHeader from '../../components/AdminNavHeader'
import { getCache, getCacheStale, setCache } from '../../lib/cache'

interface StoreOwnerRequest {
  id: string
  status: 'pending' | 'approved' | 'rejected'
  requested_at: string
  reviewed_at?: string
  review_notes?: string
  user: {
    id: string
    email: string
    name: string
    phone?: string
    created_at: string
  }
}

const StoreOwnerRequests = () => {
  const [requests, setRequests] = useState<StoreOwnerRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState<string | null>(null)

  useEffect(() => {
    loadRequests()
  }, [])

  const loadRequests = async () => {
    const cacheKey = 'admin_store_owner_requests'
    
    // Intentar cargar desde cache primero
    const cachedRequests = getCacheStale<StoreOwnerRequest[]>(cacheKey)
    if (cachedRequests && cachedRequests.length > 0) {
      setRequests(cachedRequests)
      setLoading(false)
    }

    try {
      setLoading(true)
      const response = await api.get('/admin/store-owner-requests')
      const requestsData = response.data.data || []
      setRequests(requestsData)
      
      // Guardar en cache (2 minutos de TTL - solicitudes cambian frecuentemente)
      setCache(cacheKey, requestsData, 2 * 60 * 1000)
    } catch (error: any) {
      console.error('Error loading requests:', error)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedRequests) {
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
      
      toast.error('Error al cargar las solicitudes')
    } finally {
      setLoading(false)
    }
  }

  const handleApprove = async (requestId: string) => {
    setProcessing(requestId)
    try {
      await api.post(`/admin/store-owner-requests/${requestId}/approve`, {
        reviewNotes: 'Solicitud aprobada por administrador'
      })
      toast.success('Solicitud aprobada exitosamente')
      await loadRequests() // Recargar lista
    } catch (error: any) {
      console.error('Error approving request:', error)
      toast.error(error.response?.data?.message || 'Error al aprobar la solicitud')
    } finally {
      setProcessing(null)
    }
  }

  const handleReject = async (requestId: string) => {
    const reason = prompt('Razón del rechazo (opcional):')
    if (reason === null) return // Usuario canceló

    setProcessing(requestId)
    try {
      await api.post(`/admin/store-owner-requests/${requestId}/reject`, {
        reviewNotes: reason || 'Solicitud rechazada por administrador'
      })
      toast.success('Solicitud rechazada')
      await loadRequests() // Recargar lista
    } catch (error: any) {
      console.error('Error rejecting request:', error)
      toast.error(error.response?.data?.message || 'Error al rechazar la solicitud')
    } finally {
      setProcessing(null)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'approved': return 'bg-green-100 text-green-800'
      case 'rejected': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <Clock className="h-5 w-5" />
      case 'approved': return <CheckCircle className="h-5 w-5" />
      case 'rejected': return <XCircle className="h-5 w-5" />
      default: return <Clock className="h-5 w-5" />
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Cargando solicitudes...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <AdminNavHeader
        title="Solicitudes de Dueños de Colmado"
        subtitle={`${(requests || []).filter(r => r.status === 'pending').length} pendientes`}
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {(requests || []).length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
            <div className="text-center">
              <User className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No hay solicitudes</h3>
              <p className="mt-1 text-sm text-gray-500">
                Todas las solicitudes han sido procesadas
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {(requests || []).map((request) => (
              <div key={request.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-medium text-gray-900">
                        {request.user.name}
                      </h3>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                        {getStatusIcon(request.status)}
                        <span className="ml-1 capitalize">{request.status}</span>
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                      <div>
                        <span className="font-medium">Email:</span> {request.user.email}
                      </div>
                      <div>
                        <span className="font-medium">Teléfono:</span> {request.user.phone || 'No especificado'}
                      </div>
                      <div>
                        <span className="font-medium">Solicitado:</span> {new Date(request.requested_at).toLocaleDateString('es-ES')}
                      </div>
                    </div>

                    {request.review_notes && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-md">
                        <p className="text-sm text-gray-700">
                          <span className="font-medium">Notas:</span> {request.review_notes}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="ml-4 flex flex-col space-y-2">
                    {request.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleApprove(request.id)}
                          disabled={processing === request.id}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          {processing === request.id ? 'Aprobando...' : 'Aprobar'}
                        </button>
                        <button
                          onClick={() => handleReject(request.id)}
                          disabled={processing === request.id}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          Rechazar
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default StoreOwnerRequests
